import { useCallback, useState } from 'react';

import css from '@styles/specifications';

import LinkPopup from '@components/popups/Link';

import type { Node } from 'react';
import { TContent } from '@types/popups';

export function Container({
  expanded,
  children
}: {
  expanded: boolean,
  children: Node
}) {
  return (
    <div
      className={`${css.container} specifications ${
        expanded ? '' : 'collapsed'
      }`}
    >
      {children}
    </div>
  );
}

export function Section({ children }: { children: Node }) {
  return <div>{children}</div>;
}

export function CollapsibleSection({ children }: { children: Node }) {
  return <div className={css.collapsibleSection}>{children}</div>;
}

export function Header({ children }: { children: Node }) {
  return <div className={css.header}>{children}</div>;
}

export function Subheader({ children }: { children: Node }) {
  return <div className={css.subheader}>{children}</div>;
}

export function Rows({ children }: { children: Node }) {
  return <div className={css.rows}>{children}</div>;
}

export function Row({
  children,
  onHover,
  hoverKey
}: {
  children: Node,
  onHover?: (value: ?string) => void | null,
  hoverKey?: ?string
}) {
  return (
    <div
      className={css.row}
      onMouseEnter={onHover && hoverKey && (() => onHover(hoverKey))}
      onMouseLeave={onHover && hoverKey && (() => onHover(null))}
    >
      {children}
    </div>
  );
}

export function CollapsibleRow({ children }: { children: Node }) {
  return <div className={css.collapsibleRow}>{children}</div>;
}

export function Key({ children }: { children: Node }) {
  return <div className={css.key}>{children}</div>;
}

export function LinkKey({
  children,
  href,
  onClick
}: {
  children: Node,
  href: string,
  onClick: e => void
}) {
  return (
    <div className={css.key}>
      <a href={href} target='_blank' onClick={onClick}>
        {children}
      </a>
    </div>
  );
}

export function BubbleKey({
  children,
  content
}: {
  children: Node,
  content: TContent
}) {
  return (
    <div className={css.key}>
      <LinkPopup initialContent={content}>{children}</LinkPopup>
    </div>
  );
}

export function Value({ children }: { children: Node }) {
  return <div className={css.value}>{children}</div>;
}

function ToggleExpansion({
  expanded,
  onClick,
  onKeyPress
}: {
  expanded: boolean,
  onClick: () => void,
  onKeyPress: (E: SyntheticKeyboardEvent<HTMLDivElement>) => void
}) {
  return (
    <div
      className={css[`view${expanded ? 'Less' : 'More'}`]}
      onClick={onClick}
      onKeyPress={onKeyPress}
      role='button'
      tabIndex={0}
    >
      View {expanded ? 'Less' : 'More'}
      <div className={`${css.caret} specifications--caret`} />
    </div>
  );
}

export function Specifications({
  children,
  allowExpansion
}: {
  children: Node,
  allowExpansion: boolean
}) {
  const [expanded, setExpanded] = useState(false);

  const toggleExpanded = useCallback(() => {
    setExpanded(!expanded);
  }, [expanded]);

  const handleKeypress = (e: SyntheticKeyboardEvent<HTMLDivElement>) => {
    if (e.key === 'Enter') {
      toggleExpanded();
    }
  };

  return (
    <Container expanded={expanded}>
      {children}

      {allowExpansion && (
        <ToggleExpansion
          expanded={expanded}
          onClick={toggleExpanded}
          onKeyPress={handleKeypress}
        />
      )}
    </Container>
  );
}
